import React, { useState, useEffect, useCallback } from "react";
import {
    Grid,
    Paper,
    Typography,
    Box,
    Card,
    CardContent,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Divider,
    TextField,
    MenuItem,
    Button,
} from "@mui/material";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import AssessmentIcon from "@mui/icons-material/Assessment";
import PeopleIcon from "@mui/icons-material/People";
import GetAppIcon from "@mui/icons-material/GetApp";
import MoneyOffIcon from "@mui/icons-material/MoneyOff";
import DescriptionIcon from "@mui/icons-material/Description";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import { honorariosAdminDashboardJWT } from "../../api/endpoints";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import logoSIGIF from "../../assets/sigifblue.png";

const sxChartPaper = {
    p: 3,
    borderRadius: "4px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    height: "100%",
    display: "flex",
    flexDirection: "column",
};

const sxSectionTitle = {
    fontWeight: 700,
    mb: 2,
    display: "flex",
    alignItems: "center",
    gap: 1,
};

const sxBarTrack = {
    flex: 1,
    height: 24,
    backgroundColor: "#e0e0e0",
    borderRadius: "4px",
    overflow: "hidden",
    position: "relative",
};

const sxBarFill = {
    height: "100%",
    borderRadius: "4px",
    transition: "width 0.5s ease",
    display: "flex",
    alignItems: "center",
    justifyContent: "flex-end",
    pr: "8px",
    fontSize: 11,
    fontWeight: 700,
    color: "white",
};

export default function HonorariosAdminDashboard() {
    const [loading, setLoading] = useState(true);
    const [year, setYear] = useState(new Date().getFullYear());
    const [availableYears, setAvailableYears] = useState([]);
    const [data, setData] = useState({
        evolucion_mensual: [],
        distribucion_estado: [],
        top_abogados: [],
        top_deuda: [],
        top_borradores: [],
        totales: {
            total_historico: 0,
            total_solicitudes: 0,
            promedio_solicitud: 0,
            total_pendiente: 0,
            total_en_proceso: 0,
            cant_pendiente: 0,
            cant_en_proceso: 0,
        },
    });

    const fetchDashboardData = useCallback(async (selectedYear) => {
        setLoading(true);
        try {
            const response = await honorariosAdminDashboardJWT(selectedYear);
            if (response.ok) {
                if (response.available_years && response.available_years.length > 0) {
                    setAvailableYears(response.available_years);
                } else if (availableYears.length === 0) {
                    const currentYear = new Date().getFullYear();
                    setAvailableYears(Array.from({ length: 6 }, (_, i) => currentYear - i));
                }

                setData({
                    evolucion_mensual: response.evolucion_mensual || [],
                    distribucion_estado: response.distribucion_estado || [],
                    top_abogados: response.top_abogados || [],
                    top_deuda: response.top_deuda || [],
                    top_borradores: response.top_borradores || [],
                    totales: response.totales || {
                        total_historico: 0,
                        total_solicitudes: 0,
                        promedio_solicitud: 0,
                        total_pendiente: 0,
                        total_en_proceso: 0,
                        cant_pendiente: 0,
                        cant_en_proceso: 0,
                    },
                });
            } else {
                console.error("API returned ok=false:", response);
            }
        } catch (error) {
            if (error?.code === "SESSION_EXPIRED") {
                throw error;
            }
            console.error("Error fetching admin dashboard data", error);
        } finally {
            setLoading(false);
        }
    }, [availableYears.length]);

    useEffect(() => {
        fetchDashboardData(year);
    }, [year, fetchDashboardData]);

    const formatCurrency = (value) => {
        return `$ ${parseFloat(value).toLocaleString("es-AR", {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        })}`;
    };

    const getMaxValue = (arr, key) => {
        if (arr.length === 0) return 1;
        return Math.max(...arr.map((item) => parseFloat(item[key] || 0)));
    };

    const handleExportExcel = () => {
        const wb = XLSX.utils.book_new();

        // Totales sheet
        const totalesData = [
            ["Concepto", "Valor", "Cantidad"],
            ["Total Histórico Pagado", data.totales.total_historico, data.totales.total_solicitudes],
            ["Promedio por Solicitud", data.totales.promedio_solicitud, "-"],
            ["En Proceso", data.totales.total_en_proceso, data.totales.cant_en_proceso],
            ["Pendiente", data.totales.total_pendiente, data.totales.cant_pendiente],
        ];
        const wsTotales = XLSX.utils.aoa_to_sheet(totalesData);
        XLSX.utils.book_append_sheet(wb, wsTotales, "Totales");

        // Top Abogados Sheet
        if (data.top_abogados.length > 0) {
            const topAbogadosData = [
                ["Abogado", "Cantidad Solicitudes", "Total Cobrado", "Promedio"],
                ...data.top_abogados.map(item => [item.abogado, item.cantidad, item.total, item.total / item.cantidad])
            ];
            const wsTopAbogados = XLSX.utils.aoa_to_sheet(topAbogadosData);
            XLSX.utils.book_append_sheet(wb, wsTopAbogados, "Top Cobrado");
        }

        XLSX.writeFile(wb, `Honorarios_Dashboard_${year}.xlsx`);
    };

    const loadImage = (src) => {
        return new Promise((resolve) => {
            const img = new Image();
            img.crossOrigin = "Anonymous";
            img.src = src;
            img.onload = () => {
                const canvas = document.createElement("canvas");
                canvas.width = img.width;
                canvas.height = img.height;
                const ctx = canvas.getContext("2d");
                ctx.drawImage(img, 0, 0);
                resolve(canvas.toDataURL("image/png"));
            };
            img.onerror = () => resolve(null);
        });
    };

    const handleExportPDF = async () => {
        setLoading(true);
        setTimeout(async () => {
            try {
                const doc = new jsPDF();
                const logoBase64 = await loadImage(logoSIGIF);

                if (logoBase64) {
                    doc.addImage(logoBase64, 'PNG', 14, 10, 50, 20);
                }

                doc.setFontSize(18);
                doc.setTextColor(46, 125, 50);
                doc.text(`Reporte de Honorarios - Año ${year}`, 14, 40);

                doc.setFontSize(11);
                doc.setTextColor(100);
                doc.text(`Generado el: ${new Date().toLocaleDateString()}`, 14, 48);

                // Section: Totales
                autoTable(doc, {
                    startY: 55,
                    head: [['Concepto', 'Monto', 'Cantidad Solicitudes']],
                    body: [
                        ['Total Histórico Pagado', formatCurrency(data.totales.total_historico), data.totales.total_solicitudes],
                        ['Promedio por Solicitud', formatCurrency(data.totales.promedio_solicitud), '-'],
                        ['En Proceso', formatCurrency(data.totales.total_en_proceso), data.totales.cant_en_proceso],
                        ['Pendiente', formatCurrency(data.totales.total_pendiente), data.totales.cant_pendiente],
                    ],
                    theme: 'grid',
                    headStyles: { fillColor: [46, 125, 50] },
                });

                // Section: Top Abogados
                if (data.top_abogados.length > 0) {
                    doc.text("Top 10 Abogados (Mayor Recaudación)", 14, doc.lastAutoTable.finalY + 15);
                    autoTable(doc, {
                        startY: doc.lastAutoTable.finalY + 20,
                        head: [['Abogado', 'Solicitudes', 'Total Cobrado']],
                        body: data.top_abogados.slice(0, 10).map(item => [item.abogado, item.cantidad, formatCurrency(item.total)]),
                        theme: 'striped',
                        headStyles: { fillColor: [67, 160, 71] },
                    });
                }

                // Section: Top Deuda
                if (data.top_deuda.length > 0) {
                    doc.text("Top Deuda Pendiente", 14, doc.lastAutoTable.finalY + 15);
                    autoTable(doc, {
                        startY: doc.lastAutoTable.finalY + 20,
                        head: [['Abogado', 'Solicitudes', 'Deuda Pendiente']],
                        body: data.top_deuda.slice(0, 10).map(item => [item.abogado, item.cantidad, formatCurrency(item.total)]),
                        theme: 'striped',
                        headStyles: { fillColor: [239, 83, 80] },
                    });
                }

                // Section: Top Borradores
                if (data.top_borradores.length > 0) {
                    doc.text("Más Borradores", 14, doc.lastAutoTable.finalY + 15);
                    autoTable(doc, {
                        startY: doc.lastAutoTable.finalY + 20,
                        head: [['Abogado', 'Cantidad']],
                        body: data.top_borradores.slice(0, 10).map(item => [item.abogado, item.cantidad]),
                        theme: 'striped',
                        headStyles: { fillColor: [158, 158, 158] },
                    });
                }

                doc.save(`Honorarios_Reporte_${year}.pdf`);
            } catch (error) {
                console.error("Error generating PDF", error);
            } finally {
                setLoading(false);
            }
        }, 100);
    };

    const ESTADO_COLORS = {
        0: "#FFA726", // Pendiente - Naranja
        1: "#66BB6A", // Pagada - Verde
        2: "#42A5F5", // En Curso - Azul
        4: "#AB47BC", // Cerrada - Púrpura
        3: "#9E9E9E", // Borrador - Gris
        "-1": "#EF5350" // Anulada - Rojo
    };

    // Calculate Grid Sizes
    const hasDeuda = data.top_deuda.length > 0;
    const hasBorradores = data.top_borradores.length > 0;

    let mainTableSize = 4;
    let secondaryTableSize = 4;

    const extrasCount = (hasDeuda ? 1 : 0) + (hasBorradores ? 1 : 0);

    if (extrasCount === 0) {
        mainTableSize = 12;
    } else if (extrasCount === 1) {
        mainTableSize = 6;
        secondaryTableSize = 6;
    } else {
        mainTableSize = 4;
        secondaryTableSize = 4;
    }

    const statCards = [
        { label: "Total Histórico Pagado", value: formatCurrency(data.totales.total_historico), sub: `${data.totales.total_solicitudes} solicitudes`, bg: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)" },
        { label: "Promedio por Solicitud", value: formatCurrency(data.totales.promedio_solicitud), sub: null, bg: "linear-gradient(135deg, #f093fb 0%, #f5576c 100%)" },
        { label: "En Proceso", value: formatCurrency(data.totales.total_en_proceso), sub: `${data.totales.cant_en_proceso} solicitudes`, bg: "linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)" },
        { label: "Pendiente", value: formatCurrency(data.totales.total_pendiente), sub: `${data.totales.cant_pendiente} solicitudes`, bg: "linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)" },
        { label: "Total Abogados Activos", value: data.top_abogados.length, sub: "con solicitudes pagadas", bg: "linear-gradient(135deg, #fa709a 0%, #fee140 100%)" },
    ];

    return (
        <Box sx={{ flexGrow: 1 }}>
            <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 3, flexWrap: "wrap", gap: 2 }}>
                <Typography variant="h5" style={{ fontWeight: 700, color: "#2e7d32" }}>
                    Tablero de Control
                </Typography>

                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                    <TextField
                        select
                        label="Año Fiscal"
                        value={year}
                        onChange={(e) => setYear(e.target.value)}
                        variant="outlined"
                        size="small"
                        style={{ width: 150, backgroundColor: "white" }}
                    >
                        {availableYears.length > 0 ? (
                            availableYears.map((y) => (
                                <MenuItem key={y} value={y}>
                                    {y}
                                </MenuItem>
                            ))
                        ) : (
                            <MenuItem value={year}>{year}</MenuItem>
                        )}
                    </TextField>

                    <Button
                        variant="outlined"
                        color="primary"
                        startIcon={<GetAppIcon />}
                        onClick={handleExportExcel}
                        style={{ height: 40 }}
                    >
                        Excel
                    </Button>
                    <Button
                        variant="contained"
                        style={{ backgroundColor: "#d32f2f", color: "white", height: 40 }}
                        startIcon={<PictureAsPdfIcon />}
                        onClick={handleExportPDF}
                    >
                        PDF
                    </Button>
                </Box>
            </Box>
            {loading ? (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", p: 8, minHeight: 300 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
            {/* Totales Generales - 5 cards */}
            <Grid container spacing={2} style={{ marginBottom: 24 }}>
                {statCards.map((card, i) => (
                    <Grid key={i} size={{ xs: 12, sm: 6, md: 2.4 }}>
                        <Card sx={{
                            background: card.bg,
                            color: "white",
                            borderRadius: "4px",
                            height: "100%",
                        }}>
                            <CardContent style={{ padding: 16 }}>
                                <Typography variant="body2" style={{ opacity: 0.9, marginBottom: 4, fontSize: 11 }}>
                                    {card.label}
                                </Typography>
                                <Typography variant="h5" style={{ fontWeight: 700 }}>
                                    {card.value}
                                </Typography>
                                {card.sub && (
                                    <Typography variant="caption" style={{ opacity: 0.8 }}>
                                        {card.sub}
                                    </Typography>
                                )}
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>
                <Grid container spacing={3}>
                    {/* Evolución Mensual */}
                    <Grid size={{ xs: 12, md: 8 }}>
                        <Paper sx={sxChartPaper}>
                            <Typography variant="h6" sx={sxSectionTitle}>
                                <TrendingUpIcon />
                                Evolución Mensual Detallada
                            </Typography>
                            <Divider style={{ marginBottom: 16 }} />
                            {data.evolucion_mensual.length === 0 ? (
                                <Typography variant="body2" color="text.secondary" align="center">
                                    No hay datos disponibles para el año seleccionado
                                </Typography>
                            ) : (
                                data.evolucion_mensual.map((item) => {
                                    const maxVal = getMaxValue(data.evolucion_mensual, "cobrado");
                                    const cobradoPercent = (parseFloat(item.cobrado) / maxVal) * 100;
                                    const enProcesoPercent = (parseFloat(item.en_proceso) / maxVal) * 100;

                                    return (
                                        <Box key={item.mes} sx={{ mb: 2 }}>
                                            <Typography sx={{ fontSize: 12, fontWeight: 600, mb: "4px", color: "text.secondary" }}>{item.mes}</Typography>
                                            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                                                <Box sx={sxBarTrack}>
                                                    <Box
                                                        sx={sxBarFill}
                                                        style={{
                                                            width: `${cobradoPercent}%`,
                                                            background: "linear-gradient(90deg, #66BB6A 0%, #43A047 100%)",
                                                            minWidth: parseFloat(item.cobrado) > 0 ? 30 : 0
                                                        }}
                                                    >
                                                        {item.cant_cobrado > 0 && `${item.cant_cobrado}`}
                                                    </Box>
                                                </Box>
                                                <Typography sx={{ fontSize: 12, fontWeight: 600, minWidth: 100, textAlign: "right" }}>
                                                    {formatCurrency(item.cobrado)}
                                                </Typography>
                                            </Box>
                                            {parseFloat(item.en_proceso) > 0 && (
                                                <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: "4px" }}>
                                                    <Box sx={sxBarTrack}>
                                                        <Box
                                                            sx={sxBarFill}
                                                            style={{
                                                                width: `${enProcesoPercent}%`,
                                                                background: "linear-gradient(90deg, #42A5F5 0%, #1E88E5 100%)",
                                                                minWidth: 30
                                                            }}
                                                        >
                                                            {item.cant_en_proceso > 0 && `${item.cant_en_proceso}`}
                                                        </Box>
                                                    </Box>
                                                    <Typography sx={{ fontSize: 12, fontWeight: 600, minWidth: 100, textAlign: "right" }}>
                                                        {formatCurrency(item.en_proceso)}
                                                    </Typography>
                                                </Box>
                                            )}
                                        </Box>
                                    );
                                })
                            )}
                        </Paper>
                    </Grid>

                    {/* Distribución por Estado */}
                    <Grid size={{ xs: 12, md: 4 }}>
                        <Paper sx={sxChartPaper}>
                            <Typography variant="h6" sx={sxSectionTitle}>
                                <AssessmentIcon />
                                Distribución por Estado
                            </Typography>
                            <Divider style={{ marginBottom: 16 }} />
                            {data.distribucion_estado.length === 0 ? (
                                <Typography variant="body2" color="text.secondary" align="center">
                                    No hay datos
                                </Typography>
                            ) : (
                                <>
                                    {data.distribucion_estado.map((item) => {
                                        const total = data.distribucion_estado.reduce(
                                            (sum, i) => sum + parseFloat(i.total),
                                            0
                                        );
                                        const percentage = total > 0 ? ((parseFloat(item.total) / total) * 100).toFixed(1) : 0;

                                        return (
                                            <Box key={item.id_estado} mb={2}>
                                                <Box display="flex" justifyContent="space-between" mb={0.5}>
                                                    <Typography variant="body2" style={{ fontWeight: 600 }}>
                                                        {item.estado}
                                                    </Typography>
                                                    <Typography variant="body2" color="text.secondary">
                                                        {percentage}%
                                                    </Typography>
                                                </Box>
                                                <Box
                                                    height={8}
                                                    bgcolor="#e0e0e0"
                                                    borderRadius="4px"
                                                    overflow="hidden"
                                                >
                                                    <Box
                                                        height="100%"
                                                        bgcolor={ESTADO_COLORS[item.id_estado] || "#757575"}
                                                        width={`${percentage}%`}
                                                        style={{ transition: "width 0.5s ease" }}
                                                    />
                                                </Box>
                                                <Box display="flex" justifyContent="space-between" mt={0.5}>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {item.cantidad} {item.cantidad === 1 ? "solicitud" : "solicitudes"}
                                                    </Typography>
                                                    <Typography variant="caption" style={{ fontWeight: 600 }}>
                                                        {formatCurrency(item.total)}
                                                    </Typography>
                                                </Box>
                                            </Box>
                                        );
                                    })}
                                </>
                            )}
                        </Paper>
                    </Grid>

                    {/* Top 10 Abogados - Cobrado */}
                    <Grid size={{ xs: 12, md: mainTableSize }}>
                        <Paper sx={sxChartPaper}>
                            <Typography variant="h6" sx={sxSectionTitle}>
                                <PeopleIcon />
                                Top 10 Cobrado
                            </Typography>
                            <Divider style={{ marginBottom: 16 }} />
                            <TableContainer>
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Abogado</TableCell>
                                            <TableCell align="right">Monto</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {data.top_abogados.slice(0, 10).map((abog, index) => (
                                            <TableRow key={index} hover sx={{ backgroundColor: index % 2 === 0 ? "rgba(0,0,0,0.02)" : "transparent" }}>
                                                <TableCell style={{ fontSize: 11, fontWeight: 500 }}>{abog.abogado}</TableCell>
                                                <TableCell align="right" style={{ fontSize: 11, fontWeight: 700, color: "#43A047" }}>
                                                    {formatCurrency(abog.total)}
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        </Paper>
                    </Grid>

                    {/* Top 10 Abogados - Deuda (Only show if data present) */}
                    {hasDeuda && (
                        <Grid size={{ xs: 12, md: secondaryTableSize }}>
                            <Paper sx={sxChartPaper}>
                                <Typography variant="h6" sx={sxSectionTitle}>
                                    <MoneyOffIcon color="error" />
                                    Top 10 Mayor Deuda Pendiente
                                </Typography>
                                <Divider style={{ marginBottom: 16 }} />
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Abogado</TableCell>
                                                <TableCell align="right">Deuda</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {data.top_deuda.slice(0, 10).map((abog, index) => (
                                                <TableRow key={index} hover sx={{ backgroundColor: index % 2 === 0 ? "rgba(0,0,0,0.02)" : "transparent" }}>
                                                    <TableCell style={{ fontSize: 11, fontWeight: 500 }}>{abog.abogado}</TableCell>
                                                    <TableCell align="right" style={{ fontSize: 11, fontWeight: 700, color: "#EF5350" }}>
                                                        {formatCurrency(abog.total)}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        </Grid>
                    )}

                    {/* Top 10 Abogados - Borradores (Only show if data present) */}
                    {hasBorradores && (
                        <Grid size={{ xs: 12, md: secondaryTableSize }}>
                            <Paper sx={sxChartPaper}>
                                <Typography variant="h6" sx={sxSectionTitle}>
                                    <DescriptionIcon color="disabled" />
                                    Más Borradores
                                </Typography>
                                <Divider style={{ marginBottom: 16 }} />
                                <TableContainer>
                                    <Table size="small">
                                        <TableHead>
                                            <TableRow>
                                                <TableCell>Abogado</TableCell>
                                                <TableCell align="right">Cant.</TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {data.top_borradores.slice(0, 10).map((abog, index) => (
                                                <TableRow key={index} hover sx={{ backgroundColor: index % 2 === 0 ? "rgba(0,0,0,0.02)" : "transparent" }}>
                                                    <TableCell style={{ fontSize: 11, fontWeight: 500 }}>{abog.abogado}</TableCell>
                                                    <TableCell align="right" style={{ fontSize: 12, fontWeight: 700 }}>
                                                        {abog.cantidad}
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            </Paper>
                        </Grid>
                    )}

                </Grid>
                </>
            )}
        </Box>
    );
}
